/**
 * Map GET /manual-assessment/workflows/{id}/tasks/{taskId} response to FormRenderer config.
 * Converts task.propertyValueItemList (PropertyValueItem[]) to FormFieldConfig[] and FormData.
 */

import type {
  FormFieldConfig,
  FormData,
  OptionItem,
  AsyncConfig,
  ParamFromState,
} from "../components/FormRenderer/types";
import { parseBoolean } from "../components/FormRenderer/formUtils";
import { normalizeTaskOwnersForRequest } from "../components/FormRenderer/formFieldUtils";
import type {
  WorkflowTaskDetail,
  PropertyValueItem,
  SaveTaskPayload,
  SubmitTaskPayload,
  TaskTableSavePayload,
} from "../services/regulationsService";
import type {
  TaskTableDefinition,
  TaskTableFormState,
} from "../types/manualAssessmentWorkflow";
import {
  buildTaskTableSavePayloads,
  formatTaskDurationDateForApi,
  isManualAssessmentDateField,
} from "./manualAssessmentTaskTable";
import { resolveTaskResultFromTaskTables } from "./manualAssessmentTaskResult";
import {
  buildAssignNextTaskSubmitValue,
  parseAssignNextTaskFromTaskDetail,
  type AssignNextTaskDefinition,
} from "./assignNextTaskFormConfig";

/** Options for async fields keyed by field name (e.g. department -> [{ code, name }]) */
export type OptionsByField = Record<
  string,
  Array<{ code: string; name: string }>
>;

/** Field names that must always be non-editable in the dynamic form. */
const NON_EDITABLE_OWNER_FIELDS = ["taskOwners", "taskAssignee"];

function normalizeOptions(
  item: PropertyValueItem,
  optionsByField?: OptionsByField,
): OptionItem[] {
  if (Array.isArray(item.options) && item.options.length > 0) {
    return item.options.map((o) => {
      const raw = o as Record<string, unknown>;
      const value = (raw.value ?? raw.code) as string | number;
      let label: string;
      if (typeof raw.label === "string") label = raw.label;
      else if (typeof raw.name === "string") label = raw.name;
      else label = String(value ?? "");
      return { label, value };
    });
  }
  const fieldName = item.field ? String(item.field) : "";
  const injected = optionsByField?.[fieldName];
  if (Array.isArray(injected) && injected.length > 0) {
    return injected.map((o) => ({ label: o.name || o.code, value: o.code }));
  }
  return [];
}

type CommonField = Omit<
  import("../components/FormRenderer/types").TInputCommon,
  "type"
>;

function buildTextConfig(
  common: CommonField,
  item: PropertyValueItem,
  typeLower: string,
): FormFieldConfig {
  return {
    ...common,
    type: "text",
    textArea: typeLower === "textarea",
    minLen: typeof item.minLen === "number" ? item.minLen : undefined,
    maxLen: typeof item.maxLen === "number" ? item.maxLen : undefined,
    maxRows: typeof item.maxRows === "number" ? item.maxRows : undefined,
  };
}

function normalizeAsyncConfig(
  item: PropertyValueItem,
  field: string,
): AsyncConfig | undefined {
  const ac = item.asyncConfig as
    | {
        url?: string;
        basicUrl?: string;
        params?: Array<{ value?: string; fieldName?: string }>;
        body?: Array<{ store?: string; fieldName: string; valuePath?: string }>;
        dependencies?: string[];
        requestMethod?: string;
        labelPath?: string;
        valuePath?: string;
        listDataPath?: string;
      }
    | undefined;
  if (!ac?.url) return undefined;
  const base: AsyncConfig = {
    url: ac.url,
    basicUrl: ac.basicUrl,
    params: Array.isArray(ac.params) ? ac.params : undefined,
    body: Array.isArray(ac.body) ? (ac.body as ParamFromState[]) : undefined,
    dependencies: Array.isArray(ac.dependencies) ? ac.dependencies : undefined,
    requestMethod:
      ac.requestMethod === "POST" || ac.requestMethod === "GET"
        ? ac.requestMethod
        : undefined,
    labelPath: ac.labelPath,
    valuePath: ac.valuePath,
    listDataPath: ac.listDataPath,
  };
  if (
    field === "taskAssignee" &&
    /task-assignees/i.test(ac.url) &&
    !(Array.isArray(ac.body) && ac.body.length > 0)
  ) {
    const taskAssigneesBody: ParamFromState[] = [
      { store: "formState", fieldName: "taskOwners", valuePath: "taskOwners" },
    ];
    return {
      ...base,
      requestMethod: "POST",
      body: taskAssigneesBody,
      dependencies: ["taskOwners"],
    };
  }
  return base;
}

/** When options are empty but asyncConfig exists, editability follows configurability (no force non-editable). */
function isEmptyOptionsWithAsyncConfig(options: OptionItem[], item: PropertyValueItem): boolean {
  return options.length === 0 && !!item.asyncConfig?.url;
}

function buildSelectConfig(
  common: CommonField,
  item: PropertyValueItem,
  optionsByField?: OptionsByField,
): FormFieldConfig {
  const options = normalizeOptions(item, optionsByField);
  const hasAsyncConfig = isEmptyOptionsWithAsyncConfig(options, item);
  const emptyOptionsNotEditable =
    options.length === 0 && !hasAsyncConfig
      ? { readonly: true as const, disabled: true as const, configurability: false as const }
      : {};
  const asyncConfig = normalizeAsyncConfig(item, common.field);
  return {
    ...common,
    type: "select",
    options,
    multiple: parseBoolean(item.multiple),
    ...(asyncConfig ? { asyncConfig } : {}),
    ...emptyOptionsNotEditable,
  };
}

function itemToFormFieldConfig(
  item: PropertyValueItem,
  index: number,
  optionsByField?: OptionsByField,
): FormFieldConfig | null {
  const field = item.field != null ? String(item.field) : `field_${index}`;
  const label = item.label != null ? String(item.label) : field;
  const originlabel =
    item.originlabel != null ? String(item.originlabel) : label;
  const order = typeof item.order === "number" ? item.order : index;
  const required = parseBoolean(item.required);
  const readonly = parseBoolean(item.readonly);
  const hide = parseBoolean(item.hide);
  const size =
    item.size === "small" || item.size === "medium" ? item.size : "medium";
  const isOwnerField = NON_EDITABLE_OWNER_FIELDS.includes(field);
  let configurability: boolean | undefined;
  if (isOwnerField) {
    configurability = false;
  } else {
    const raw = item.configurability;
    configurability = parseBoolean(raw)
      ? true
      : raw === false || raw === 0 || (typeof raw === "string" && raw.toLowerCase() === "false")
        ? false
        : undefined;
  }
  const common: CommonField = {
    field,
    originlabel,
    label,
    required,
    readonly: isOwnerField ? true : readonly,
    disabled: isOwnerField ? true : readonly,
    order,
    description:
      typeof item.description === "string" ? item.description : undefined,
    placeholder:
      typeof item.placeholder === "string" ? item.placeholder : undefined,
    hide,
    size,
    field_xs: typeof item.field_xs === "number" ? item.field_xs : undefined,
    field_md: typeof item.field_md === "number" ? item.field_md : undefined,
    field_lg: typeof item.field_lg === "number" ? item.field_lg : undefined,
    field_xl: typeof item.field_xl === "number" ? item.field_xl : undefined,
    configurability,
  };

  const apiType = item.type ?? "text";
  const typeLower = String(apiType).toLowerCase();
  if (hide) return null;

  const config = buildConfigByType(typeLower, common, item, optionsByField);
  return config ?? { ...common, type: "text" as const, textArea: false };
}

/** Maps API field type to FormFieldConfig; one branch per supported type. */
// eslint-disable-next-line sonarjs/cognitive-complexity -- type dispatcher, each branch is one API type
function buildConfigByType(
  typeLower: string,
  common: CommonField,
  item: PropertyValueItem,
  optionsByField?: OptionsByField,
): FormFieldConfig | null {
  if (["text", "textarea", "string"].includes(typeLower)) {
    return buildTextConfig(common, item, typeLower);
  }
  if (typeLower === "number") {
    return {
      ...common,
      type: "number",
      min: typeof item.min === "number" ? item.min : undefined,
      max: typeof item.max === "number" ? item.max : undefined,
      decimalPlaces:
        typeof item.decimalPlaces === "number" ? item.decimalPlaces : undefined,
    };
  }
  if (typeLower === "date") {
    const isDueDate = common.field === "assessment_due_by" || common.label?.toLowerCase().includes("due by");
    const isAssessmentDate = common.field === "assessment_date" || common.label?.toLowerCase().includes("assessment date");
    const isAssessmentHistoryDate = common.field === "assessment_history_date" || common.label?.toLowerCase().includes("history");
    return {
      ...common,
      type: "date",
      timestamp: parseBoolean(item.timestamp),
      dateFormat:
        typeof item.dateFormat === "string" ? item.dateFormat : "DD-MMM-YYYY",
      // Assessment Date should not be in the future
      maxDate: isAssessmentDate ? new Date().getTime() : undefined,
      // Due dates should not be in the past
      minDate: isDueDate ? new Date().setHours(0, 0, 0, 0) : undefined,
      // Assessment History Date should have no restrictions (already handled by undefined min/max)
    };
  }
  if (typeLower === "checkbox") return { ...common, type: "checkbox" };
  if (typeLower === "select" || typeLower === "dropdown") {
    return buildSelectConfig(common, item, optionsByField);
  }
  if (typeLower === "radio") {
    const options = normalizeOptions(item, optionsByField);
    const hasAsyncConfig = isEmptyOptionsWithAsyncConfig(options, item);
    const emptyOptionsNotEditable =
      options.length === 0 && !hasAsyncConfig
        ? { readonly: true as const, disabled: true as const, configurability: false as const }
        : {};
    const asyncConfig = normalizeAsyncConfig(item, common.field);
    return {
      ...common,
      type: "radio",
      options,
      inline: parseBoolean(item.inline),
      ...(asyncConfig ? { asyncConfig } : {}),
      ...emptyOptionsNotEditable,
    };
  }
  if (typeLower === "checkboxgroups") {
    const options = normalizeOptions(item, optionsByField);
    const hasAsyncConfig = isEmptyOptionsWithAsyncConfig(options, item);
    const emptyOptionsNotEditable =
      options.length === 0 && !hasAsyncConfig
        ? { readonly: true as const, disabled: true as const, configurability: false as const }
        : {};
    const asyncConfig = normalizeAsyncConfig(item, common.field);
    return {
      ...common,
      type: "checkboxGroups",
      options,
      inline: parseBoolean(item.inline),
      multiple: item.multiple === undefined ? true : parseBoolean(item.multiple),
      ...(asyncConfig ? { asyncConfig } : {}),
      ...emptyOptionsNotEditable,
    };
  }
  if (typeLower === "link") {
    return {
      ...common,
      type: "link",
      labelPath: item.labelPath as string | undefined,
      valuePath: item.valuePath as string | undefined,
    };
  }
  return null;
}

const TASK_OWNER_GROUP_KEYS = ["taskUsers", "taskGroups", "taskRoles"] as const;

function fieldValueToIds(v: unknown): string[] {
  if (v === null || v === undefined) return [];
  if (typeof v === "string") return v ? [v] : [];
  if (typeof v === "number") return [String(v)];
  if (typeof v === "object" && v !== null && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    const parts: string[] = [];
    for (const key of TASK_OWNER_GROUP_KEYS) {
      const arr = o[key];
      if (Array.isArray(arr)) {
        for (const id of arr) {
          if (id != null && id !== "") parts.push(String(id));
        }
      }
    }
    return parts;
  }
  return [];
}

/** Check if value is task-owner structure { taskUsers, taskGroups, taskRoles } */
function isTaskOwnerStructure(v: unknown): v is Record<string, unknown> {
  if (v == null || typeof v !== "object" || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  return "taskUsers" in o || "taskGroups" in o || "taskRoles" in o;
}

function fieldValueToFormValue(item: PropertyValueItem): unknown {
  const v = item.fieldValue;
  if (v === null || v === undefined) return undefined;
  const field = item.field ? String(item.field) : "";
  const ac = (item as { asyncConfig?: { url?: string } }).asyncConfig;
  const hasAsyncConfig = !!ac?.url;

  if (hasAsyncConfig && (field === "taskOwners" || field === "taskAssignee")) {
    if (field === "taskOwners" && isTaskOwnerStructure(v)) {
      return v;
    }
    const ids = fieldValueToIds(v);
    return field === "taskAssignee" && ids.length > 0 ? ids[0] : ids;
  }

  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return v;
  if (Array.isArray(v)) return v;
  if (typeof v === "object" && v !== null) return v;
  if (typeof v === "symbol") return undefined;
  return undefined;
}

export interface TaskDetailFormConfig {
  fields: FormFieldConfig[];
  initialValue: FormData;
  /** Card title from task (e.g. taskInstanceName) */
  title?: string;
}

/**
 * Convert workflow task detail to FormRenderer fields and initial values.
 * Uses optionsByField to inject options for async/select fields (e.g. Department from concepts).
 */
export function taskDetailToFormConfig(
  taskDetail: WorkflowTaskDetail | null,
  optionsByField?: OptionsByField,
): TaskDetailFormConfig {
  const list = taskDetail?.task?.propertyValueItemList;
  if (!Array.isArray(list) || list.length === 0) {
    return { fields: [], initialValue: {}, title: undefined };
  }

  const fields: FormFieldConfig[] = [];
  const initialValue: FormData = {};

  list.forEach((item, index) => {
    const config = itemToFormFieldConfig(item, index, optionsByField);
    if (config) {
      fields.push(config);
      const key = config.field;
      const val = fieldValueToFormValue(item);
      if (val !== undefined) initialValue[key] = val;
    }
  });

  const title =
    taskDetail?.task?.taskInstanceName != null
      ? String(taskDetail.task.taskInstanceName)
      : undefined;

  return { fields, initialValue, title };
}

/** Value for submit from task item (fieldValue). */
function itemValueForSubmit(item: PropertyValueItem): unknown {
  const v = item.fieldValue;
  if (v === null || v === undefined) return null;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return v;
  if (Array.isArray(v)) return v;
  if (typeof v === "object" && v !== null) return v;
  return null;
}

/** Resolve value for one field: formData first, else task's fieldValue. */
function resolveFieldValue(
  item: PropertyValueItem,
  formData: Record<string, unknown>,
): { field: string; value: unknown } | null {
  const field = item.field != null ? String(item.field) : "";
  if (!field) return null;
  const value =
    formData[field] !== undefined ? formData[field] : itemValueForSubmit(item);
  return { field, value };
}

function isAttachmentPropertyItem(item: PropertyValueItem): boolean {
  return String(item.type ?? "").trim().toLowerCase() === "attachment";
}

/** Already written with dedicated normalization, or sent as top-level `taskResult`. */
const CUSTOM_PROPERTY_MAP_HANDLED_FIELDS = new Set([
  "taskOwners",
  "taskAssignee",
  "openCommonNotifications",
  "taskResult",
]);

function shouldIncludePropertyItemInCustomPropertyMap(
  item: PropertyValueItem,
): boolean {
  if (isAttachmentPropertyItem(item)) return true;
  const field = item.field != null ? String(item.field) : "";
  return !CUSTOM_PROPERTY_MAP_HANDLED_FIELDS.has(field);
}

/** Attachment lists are always sent (including `[]`) so the API can clear deletions. */
function normalizeAttachmentListValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}


/** Normalize taskResult from RJSF select (string | { value } | array). */
export function normalizeTaskResultFormValue(value: unknown): string | null {
  if (value === undefined || value === null || value === "") return null;
  if (Array.isArray(value)) {
    const parts = value
      .map((part) => normalizeTaskResultFormValue(part))
      .filter((part): part is string => part != null && part !== "");
    return parts.length > 0 ? parts.join(",") : null;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (
      record.value !== undefined &&
      record.value !== null &&
      record.value !== ""
    ) {
      return String(record.value);
    }
    return null;
  }
  return String(value);
}

/**
 * When task definition's `taskResult` field has exactly one option (often hidden /
 * systemDefault), submit uses that option's value — e.g. `"completed"`.
 */
export function getSingleTaskResultOptionValue(
  taskDetail: WorkflowTaskDetail | null | undefined,
): string | null {
  const list = taskDetail?.task?.propertyValueItemList;
  if (!Array.isArray(list)) return null;

  const item = list.find(
    (row) => row.field != null && String(row.field) === "taskResult",
  );
  if (!item || !Array.isArray(item.options) || item.options.length !== 1) {
    return null;
  }

  const option = item.options[0] as Record<string, unknown>;
  const value = option.value ?? option.code;
  if (value === undefined || value === null || value === "") return null;
  return String(value);
}

function taskDefinitionHasField(
  taskDetail: WorkflowTaskDetail | null | undefined,
  field: string,
): boolean {
  const list = taskDetail?.task?.propertyValueItemList;
  if (!Array.isArray(list)) return false;
  return list.some(
    (item) => item.field != null && String(item.field) === field,
  );
}

function resolveImpactFoundTaskResult(
  formData: Record<string, unknown>,
): string | null {
  const raw = formData.impacted ?? formData.impact_found;
  if (raw === undefined || raw === null || raw === "") return null;
  const normalized =
    normalizeTaskResultFormValue(raw)?.toLowerCase() ?? String(raw).toLowerCase();
  if (normalized === "yes" || normalized === "true") return "impact_found_yes";
  if (normalized === "no" || normalized === "false") return "impact_found_no";
  return null;
}

export type ResolveSubmitTaskResult =
  | { ok: true; taskResult: string }
  | { ok: false; message: string };

/**
 * Resolve top-level submit `taskResult` from form data and footer action.
 * Uses whatever value the taskResult dropdown provides (backend-driven options).
 * Approve/Reject buttons still map to approved/rejected.
 */
export function resolveSubmitTaskResult(
  formData: Record<string, unknown>,
  taskDetail: WorkflowTaskDetail | null | undefined,
  options?: { action?: string },
): ResolveSubmitTaskResult {
  const action = options?.action;
  if (action === "approve") {
    return { ok: true, taskResult: "approved" };
  }
  if (action === "reject") {
    return { ok: true, taskResult: "rejected" };
  }

  const fromForm = normalizeTaskResultFormValue(formData.taskResult);
  if (fromForm) {
    return { ok: true, taskResult: fromForm };
  }

  const fromImpactFound = resolveImpactFoundTaskResult(formData);
  if (fromImpactFound) {
    return { ok: true, taskResult: fromImpactFound };
  }

  // Legacy FormRenderer only: yes/no field when task def has no taskResult row.
  if (
    !taskDefinitionHasField(taskDetail ?? null, "taskResult") &&
    taskDefinitionHasField(taskDetail ?? null, "assessment_required")
  ) {
    const legacy = formData.assessment_required;
    if (legacy === "yes" || legacy === true) {
      return { ok: true, taskResult: "assessment_required" };
    }
    if (legacy === "no" || legacy === false) {
      return { ok: true, taskResult: "assessment_not_required" };
    }
  }

  return {
    ok: false,
    message:
      "Task outcome is required. Please select a result before submitting.",
  };
}

/**
 * Build customPropertyMap for task save/submit.
 * Same shape for both APIs: every property item (including systemDefault)
 * plus owner/notification defaults (hidden empty custom fields omitted).
 */
export function buildCustomPropertyMap(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
): Record<string, unknown> {
  return buildSaveCustomPropertyMap(taskDetail, formData);
}

/** True when GET task detail includes a non-empty `taskTables` array. */
export function taskDetailHasTaskTables(
  taskDetail: WorkflowTaskDetail | null | undefined,
): boolean {
  const raw = taskDetail?.taskTables;
  return Array.isArray(raw) && raw.length > 0;
}

/**
 * customPropertyMap for submit when the task has taskTables — same builder as save.
 */
export function buildSubmitCustomPropertyMapForTaskTables(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
): Record<string, unknown> {
  return buildSaveCustomPropertyMap(taskDetail, formData);
}

function buildSubmitTaskTablesPayload(
  tables: readonly TaskTableDefinition[] | null | undefined,
  state: TaskTableFormState,
): TaskTableSavePayload | TaskTableSavePayload[] | undefined {
  const normalizedTables = tables ?? [];
  if (normalizedTables.length === 0) return undefined;

  const payloads = buildTaskTableSavePayloads(normalizedTables, state);
  if (payloads.length > 0) {
    return payloads.length === 1 ? payloads[0] : payloads;
  }

  const shells = normalizedTables
    .filter((table) => Boolean(table.name))
    .map((table) => ({
      name: table.name,
      tasks: table.tasks ?? [],
      fieldsValueMap: [],
    }));

  if (shells.length === 0) return undefined;
  return shells.length === 1 ? shells[0] : shells;
}

function resolveLegacySubmitTaskResult(
  formData: Record<string, unknown>,
  taskDetail: WorkflowTaskDetail | null | undefined,
): string {
  if (
    formData.taskResult !== undefined &&
    formData.taskResult !== null &&
    formData.taskResult !== ""
  ) {
    return Array.isArray(formData.taskResult)
      ? formData.taskResult.join(",")
      : String(formData.taskResult);
  }

  const singleTaskResultOption = getSingleTaskResultOptionValue(taskDetail ?? null);
  if (singleTaskResultOption) {
    return singleTaskResultOption;
  }

  return formData.assessment_required === "yes"
    ? "assessment_required"
    : "assessment_not_required";
}

export type BuildSubmitTaskPayloadInput = {
  taskId: string;
  taskDefinitionId: string;
  taskDetail: WorkflowTaskDetail | null | undefined;
  formData: Record<string, unknown>;
  taskTables?: TaskTableDefinition[];
  taskTableState?: TaskTableFormState;
  action?: string;
  assignNextTask?: AssignNextTaskDefinition;
  assignNextTaskFormData?: Record<string, unknown>;
  /** From ESignValidation success callback when Login Authentication returns an id. */
  esignId?: string;
};

/** Assemble POST /manual-assessment/workflows/{workflow_id}/tasks/{task_id}/submit body (legacy or taskTables shape). */
export function buildSubmitTaskPayload(
  input: BuildSubmitTaskPayloadInput,
): SubmitTaskPayload {
  const {
    taskId,
    taskDefinitionId,
    taskDetail,
    formData,
    taskTables,
    taskTableState = {},
    action,
    esignId,
  } = input;

  const esignIdField = esignId ? { eSign: { esignId } } : {};

  const assignNextTaskPayload = buildAssignNextTaskSubmitValue(
    input.assignNextTask ?? parseAssignNextTaskFromTaskDetail(taskDetail),
    // Never fall back to the main formData — it may contain Current Assignee
    // under the same `taskAssignee` key as the assign-next form.
    input.assignNextTaskFormData ?? {},
  );

  const hasTaskTables = taskDetailHasTaskTables(taskDetail);

  let taskResult: string | string[];
  if (action === "approve") {
    taskResult = "approved";
  } else if (action === "reject") {
    taskResult = "rejected";
  } else if (hasTaskTables) {
    const tableTaskResult = resolveTaskResultFromTaskTables(
      taskTables,
      taskTableState,
    );
    taskResult = tableTaskResult ?? "";
  } else {
    taskResult = resolveLegacySubmitTaskResult(formData, taskDetail);
  }

  if (hasTaskTables) {
    const payload: SubmitTaskPayload = {
      taskId,
      taskDefinitionId,
      taskResult,
      customPropertyMap: buildSubmitCustomPropertyMapForTaskTables(
        taskDetail ?? null,
        formData,
      ),
      taskCustomAssignee: resolveTaskOwnersValue(taskDetail ?? null, formData),
    };

    const taskTablesPayload = buildSubmitTaskTablesPayload(
      taskTables,
      taskTableState,
    );
    if (taskTablesPayload) {
      payload.taskTables = taskTablesPayload;
    }
    if (assignNextTaskPayload) {
      payload.assignNextTask = assignNextTaskPayload;
    }

    return { ...payload, ...esignIdField };
  }

  const taskResultScalar = Array.isArray(taskResult)
    ? taskResult.join(",")
    : taskResult;

  return {
    taskId,
    taskDefinitionId,
    taskResult: taskResultScalar,
    customPropertyMap: buildSaveCustomPropertyMap(taskDetail ?? null, formData),
    ...(assignNextTaskPayload ? { assignNextTask: assignNextTaskPayload } : {}),
    ...esignIdField,
  };
}

function findPropertyItem(
  taskDetail: WorkflowTaskDetail | null,
  field: string,
): PropertyValueItem | undefined {
  const list = taskDetail?.task?.propertyValueItemList;
  if (!Array.isArray(list)) return undefined;
  return list.find((item) => item.field === field);
}

function isEmptyCustomPropertyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function shouldIncludeCustomField(
  item: PropertyValueItem,
  value: unknown,
): boolean {
  if (parseBoolean(item.hide) && isEmptyCustomPropertyValue(value)) {
    return false;
  }
  return true;
}

function resolveTaskOwnersValue(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
): SaveTaskPayload["taskCustomAssignee"] {
  if (formData.taskOwners !== undefined) {
    return normalizeTaskOwnersForRequest(formData.taskOwners);
  }
  const item = findPropertyItem(taskDetail, "taskOwners");
  if (item?.fieldValue !== undefined) {
    return normalizeTaskOwnersForRequest(item.fieldValue);
  }
  return { taskUsers: [], taskGroups: [], taskRoles: [] };
}

/** Workflow save/init/submit APIs expect a single assignee id string, not an array. */
export function normalizeTaskAssigneeForRequest(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (isTaskOwnerStructure(value)) {
    const ids = fieldValueToIds(value);
    return ids.length > 0 ? ids[0] : null;
  }
  if (Array.isArray(value)) {
    const first = value.find((entry) => entry != null && entry !== "");
    return first != null ? normalizeTaskAssigneeForRequest(first) : null;
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (obj.value != null && obj.value !== "") return String(obj.value);
    if (obj.id != null && obj.id !== "") return String(obj.id);
  }
  return String(value);
}

function resolveTaskAssigneeValue(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
): string | null {
  if (formData.taskAssignee !== undefined) {
    return normalizeTaskAssigneeForRequest(formData.taskAssignee);
  }
  const item = findPropertyItem(taskDetail, "taskAssignee");
  return normalizeTaskAssigneeForRequest(item?.fieldValue);
}

function resolveOpenCommonNotificationsValue(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
): boolean {
  if (formData.openCommonNotifications !== undefined) {
    return Boolean(formData.openCommonNotifications);
  }
  const item = findPropertyItem(taskDetail, "openCommonNotifications");
  if (item?.fieldValue !== undefined) {
    return Boolean(item.fieldValue);
  }
  return false;
}

function hasTaskOwnersField(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
): boolean {
  return (
    formData.taskOwners !== undefined ||
    findPropertyItem(taskDetail, "taskOwners") != null
  );
}

function hasTaskAssigneeField(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
): boolean {
  return (
    formData.taskAssignee !== undefined ||
    findPropertyItem(taskDetail, "taskAssignee") != null
  );
}

/**
 * Build customPropertyMap for POST save and submit task APIs.
 * Includes systemDefault fields and omits hidden empty custom fields.
 */
export function buildSaveCustomPropertyMap(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const list = taskDetail?.task?.propertyValueItemList;

  if (hasTaskOwnersField(taskDetail, formData)) {
    out.taskOwners = resolveTaskOwnersValue(taskDetail, formData);
  }
  if (hasTaskAssigneeField(taskDetail, formData)) {
    out.taskAssignee = resolveTaskAssigneeValue(taskDetail, formData);
  }
  out.openCommonNotifications = resolveOpenCommonNotificationsValue(
    taskDetail,
    formData,
  );

  if (Array.isArray(list)) {
    for (const item of list) {
      if (!shouldIncludePropertyItemInCustomPropertyMap(item)) continue;
      const resolved = resolveFieldValue(item, formData);
      if (!resolved) continue;
      if (isAttachmentPropertyItem(item)) {
        out[resolved.field] = normalizeAttachmentListValue(resolved.value);
        continue;
      }
      if (!shouldIncludeCustomField(item, resolved.value)) {
        continue;
      }
      out[resolved.field] = isManualAssessmentDateField(
        resolved.field,
        item.type,
      )
        ? formatTaskDurationDateForApi(resolved.value)
        : resolved.value;
    }
  }

  return out;
}

export interface SaveTaskIdentity {
  processAssociationId: string;
  taskDefinitionId: string;
  taskInstanceUniqueId: string;
}

/** Full request body for POST /manual-assessment/workflows/{workflow_id}/tasks/{task_id}/save. */
export function buildSaveTaskPayload(
  taskDetail: WorkflowTaskDetail | null,
  formData: Record<string, unknown>,
  ids: SaveTaskIdentity,
  taskTables?: TaskTableSavePayload | TaskTableSavePayload[],
  options?: {
    assignNextTask?: AssignNextTaskDefinition;
    assignNextTaskFormData?: Record<string, unknown>;
    /** Live table defs + edit state — used to derive `taskResult` (same as submit). */
    taskTableDefinitions?: TaskTableDefinition[];
    taskTableState?: TaskTableFormState;
  },
): SaveTaskPayload {
  const payload: SaveTaskPayload = {
    ...ids,
    milestone: false,
    milestoneName: "",
    milestoneDate: "",
    customPropertyMap: buildSaveCustomPropertyMap(taskDetail, formData),
  };

  if (hasTaskOwnersField(taskDetail, formData)) {
    payload.taskCustomAssignee = resolveTaskOwnersValue(taskDetail, formData);
  }

  if (taskTables) {
    payload.taskTables = taskTables;
  }

  if (taskDetailHasTaskTables(taskDetail)) {
    const tableTaskResult = resolveTaskResultFromTaskTables(
      options?.taskTableDefinitions,
      options?.taskTableState ?? {},
    );
    if (tableTaskResult != null) {
      payload.taskResult = tableTaskResult;
    }
  }

  if (payload.taskResult == null) {
    const formTaskResult = normalizeTaskResultFormValue(formData.taskResult);
    if (formTaskResult) {
      payload.taskResult = formTaskResult;
    }
  }

  const assignNextTaskPayload = buildAssignNextTaskSubmitValue(
    options?.assignNextTask ?? parseAssignNextTaskFromTaskDetail(taskDetail),
    // Never fall back to the main formData — it may contain Current Assignee
    // under the same `taskAssignee` key as the assign-next form.
    options?.assignNextTaskFormData ?? {},
  );
  if (assignNextTaskPayload) {
    payload.assignNextTask = assignNextTaskPayload;
  }

  return payload;
}

function isEmptyTaskResultFieldValue(value: unknown): boolean {
  if (value == null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Value to show on the dynamic-form `taskResult` item after save.
 * Keeps arrays so multi-select options still match; unwraps `{ value }`.
 */
function taskResultFieldValueForRefill(value: unknown): unknown {
  if (Array.isArray(value)) return value;
  if (value != null && typeof value === "object") {
    return normalizeTaskResultFormValue(value);
  }
  return value;
}

/**
 * After save, write the dynamic-form `taskResult` back onto its property item
 * when the refetched task detail left `fieldValue` empty.
 * Table-derived outcomes stay on the save payload; this only refills the form item.
 */
export function applySavedTaskResultToTaskDetail<T extends WorkflowTaskDetail>(
  taskDetail: T,
  savedFormTaskResult: unknown,
): T {
  const fieldValue = taskResultFieldValueForRefill(savedFormTaskResult);
  if (isEmptyTaskResultFieldValue(fieldValue)) return taskDetail;

  const list = taskDetail.task?.propertyValueItemList;
  if (!Array.isArray(list)) return taskDetail;

  const index = list.findIndex(
    (row) => row.field != null && String(row.field) === "taskResult",
  );
  if (index < 0) return taskDetail;
  if (!isEmptyTaskResultFieldValue(list[index]?.fieldValue)) return taskDetail;

  const nextList = list.slice();
  nextList[index] = { ...list[index], fieldValue };
  return {
    ...taskDetail,
    task: {
      ...taskDetail.task,
      propertyValueItemList: nextList,
    },
  };
}

/**
 * Map dynamic-form `taskOwners` field value to save API `taskCustomAssignee`.
 * Returns undefined when owners are absent or empty.
 */
export function buildTaskCustomAssigneeFromFormData(
  formData: Record<string, unknown>,
): SaveTaskPayload["taskCustomAssignee"] | undefined {
  if (formData.taskOwners === undefined) return undefined;
  const assignee = normalizeTaskOwnersForRequest(formData.taskOwners);
  const hasAny =
    assignee.taskUsers.length > 0 ||
    assignee.taskGroups.length > 0 ||
    assignee.taskRoles.length > 0;
  return hasAny ? assignee : undefined;
}
